// src/context/hub/HubContext.tsx
import React, { createContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { HubState, HubContextType, HubInfo } from './HubTypes';
import { hubService } from '../../api/services';
import { useAuth } from '../auth';

// Initial state for hub context
const initialState: HubState = {
  hubs: [],
  currentHubId: null,
  loading: false,
  error: null
};

// Define the action types
type HubAction = 
  | { type: 'FETCH_HUBS_START' }
  | { type: 'FETCH_HUBS_SUCCESS'; hubs: HubInfo[] }
  | { type: 'FETCH_HUBS_FAIL'; error: string }
  | { type: 'CHANGE_HUB'; hubId: string }
  | { type: 'CLEAR_ERROR' };

// Create the reducer function
const hubReducer = (state: HubState, action: HubAction): HubState => {
  console.log('[HubReducer] Action:', action.type, action);
  
  switch (action.type) {
    case 'FETCH_HUBS_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'FETCH_HUBS_SUCCESS':
      console.log('[HubReducer] Fetched hubs:', action.hubs);
      // Only set default hub if we don't already have one and there are hubs available
      const newHubId = state.currentHubId || (action.hubs.length > 0 ? action.hubs[0].hubId : null);
      console.log('[HubReducer] Setting currentHubId to:', newHubId);
      
      return {
        ...state,
        hubs: action.hubs,
        currentHubId: newHubId,
        loading: false,
        error: null
      };
    case 'FETCH_HUBS_FAIL':
      return {
        ...state,
        loading: false,
        error: action.error
      };
    case 'CHANGE_HUB':
      console.log('[HubReducer] Changing hub to:', action.hubId);
      return {
        ...state,
        currentHubId: action.hubId
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Create the context with a default value
export const HubContext = createContext<HubContextType>({
  ...initialState,
  fetchHubs: async () => {},
  changeHub: () => {},
  clearError: () => {}
});

interface HubProviderProps {
  children: React.ReactNode;
}

// Create the provider component
export const HubProvider: React.FC<HubProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(hubReducer, initialState);
  const { isAuthenticated, userData } = useAuth();
  const fetchInFlightRef = useRef(false);

  console.log('[HubProvider] Auth state:', { isAuthenticated, userData, hubs: state.hubs, currentHubId: state.currentHubId });

  // Fetch hubs method
  const fetchHubs = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('[fetchHubs] Not authenticated, skipping fetch');
      return;
    }
    if (fetchInFlightRef.current) {
      console.log('[fetchHubs] Fetch already in flight, skipping duplicate request');
      return;
    }
    
    // Allow fetching even without userData by using a default userId
    const userId = userData?.id || 'default-user';
    console.log('[fetchHubs] Starting fetch for user:', userId);
    
    try {
      fetchInFlightRef.current = true;
      dispatch({ type: 'FETCH_HUBS_START' });
      
      // Even without user data, we can fetch mock hubs in development mode
      const hubsData = await hubService.getHubsForUser(userId);
      
      console.log('[fetchHubs] Received hub data:', hubsData);
      
      if (hubsData.length > 0) {
        dispatch({ type: 'FETCH_HUBS_SUCCESS', hubs: hubsData });
      } else {
        console.error('[fetchHubs] Received empty hubs array');
        dispatch({ 
          type: 'FETCH_HUBS_FAIL', 
          error: 'No hubs available for this user.' 
        });
      }
    } catch (error) {
      console.error('[fetchHubs] Failed to fetch hubs:', error);
      dispatch({ 
        type: 'FETCH_HUBS_FAIL', 
        error: 'Failed to load hubs. Please try again later.' 
      });
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [isAuthenticated, userData]);

  // Change hub method
  const changeHub = (hubId: string) => {
    console.log('[changeHub] Request to change hub to:', hubId);
    
    // Only dispatch if the hub is actually changing
    if (hubId !== state.currentHubId) {
      console.log('[changeHub] Changing hub from', state.currentHubId, 'to', hubId);
      dispatch({ type: 'CHANGE_HUB', hubId });
    } else {
      console.log('[changeHub] Hub already set to', hubId, '- no change needed');
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Auto-fetch hubs when user is authenticated
  useEffect(() => {
    console.log('[HubProvider] Auth state changed:', { isAuthenticated, userData });
    if (isAuthenticated) {
      console.log('[HubProvider] User is authenticated, fetching hubs');
      fetchHubs();
    }
  }, [isAuthenticated, userData, fetchHubs]);

  // Context value
  const contextValue: HubContextType = {
    ...state,
    fetchHubs,
    changeHub,
    clearError
  };

  return (
    <HubContext.Provider value={contextValue}>
      {children}
    </HubContext.Provider>
  );
}; 
